import React from 'react';
import PropTypes from 'prop-types';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashFilter from 'lodash/filter';
import MoreMenuItem from 'modules/shell/components/MoreMenu/MoreMenuItem';
import { Divider, Typography, List, ListItem, ButtonBase, Grid } from '@material-ui/core';
import Checkbox from '../Checkbox';
import BackToTop from '../BackToTop';
import { getAlphaBetIndexedCategories } from '../../utils/designersNavigation';

const DesignerNavigation = ({
  classes,
  history,
  designerCategory,
  searchPage,
  searchValue,
  checkboxMode,
  checkboxClickHandler,
  checkedItems,
}) => {
  const [filteredResults, setFilteredResults] = React.useState(getAlphaBetIndexedCategories(designerCategory));
  const [searchResults, setSearchResults] = React.useState([]);
  const isValidSearch = searchPage && searchValue.length > 0;
  const alphaList = React.useRef(null);

  React.useEffect(() => {
    setFilteredResults(getAlphaBetIndexedCategories(designerCategory));
  }, [designerCategory]);

  React.useEffect(() => {
    let results = [];
    if (searchPage && searchValue.length > 0) {
      const firstSearchChar = searchValue[0].toUpperCase() !== searchValue[0].toLowerCase() ? searchValue[0] : '#';
      results = filteredResults[firstSearchChar.toUpperCase()] || [];
      if (results.length) {
        results = lodashFilter(results, (category) => {
          return category?.label.toLowerCase().includes(searchValue.toLowerCase());
        });
      }
    }
    setSearchResults(results);
  }, [searchValue, filteredResults]);

  const handleOnClick = (e) => {
    document.getElementById(e).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  };

  const menuClickHandler = ({ children, id, isLeafNode, driveToSubcategoryId }) => {
    const plpId = driveToSubcategoryId || id;
    const nextPage = isLeafNode || !children || !children.length ? 'listing' : 'catalog/browse';
    history.push(`/products/${nextPage}/${plpId}`);
  };

  const renderIndexContents = (key, value, renderWithTitle) => (
    <div key={key} id={key} className={classes.container}>
      {renderWithTitle && (
        <>
          <Typography variant="h4" className={classes.title}>
            {key}
          </Typography>
          <Divider variant="middle" className={classes.divider} />
        </>
      )}
      {checkboxMode && (
        <Grid container className={classes.checkboxContainer} direction="column">
          {value.map(({ label, title, value }) => (
            <Checkbox
              key={label || value}
              checked={checkedItems.includes(label) || checkedItems.includes(value)}
              data-testid="designer-navigation-checkbox"
              name={label || value}
              onChange={checkboxClickHandler}
              label={title || value}
              classes={{ root: classes.checkbox, labelRoot: classes.checkboxLabel }}
            />
          ))}
        </Grid>
      )}
      {!checkboxMode &&
        value.map(({ label, id, children, isLeafNode, driveToSubcategoryId }, index) => (
          <MoreMenuItem
            key={id}
            title={label}
            onClick={() => menuClickHandler({ children, id, isLeafNode, driveToSubcategoryId })}
            data-testid={`designer-catalog-menu-${index}-link`}
            showCircle={false}
            classMenu={classes.catalogMenuText}
          />
        ))}
    </div>
  );

  return (
    <>
      {!searchPage && (
        <BackToTop targetRef={alphaList} classes={{ backToTop: classes.backToTop }}>
          {!lodashIsEmpty(filteredResults) && (
            <List id="alphaList" className={classes.list} ref={alphaList}>
              <ListItem className={classes.listItem}>
                {Object.keys(filteredResults).map((alphabets) => (
                  <ButtonBase
                    key={alphabets}
                    onClick={() => handleOnClick(alphabets)}
                    className={classes.listItemText}
                    disabled={!filteredResults[alphabets].length}
                  >
                    <Typography
                      color="primary"
                      className={!filteredResults[alphabets].length ? classes.disabled : classes.alphaIndexTitle}
                      variant="h3"
                    >
                      {alphabets}
                    </Typography>
                  </ButtonBase>
                ))}
              </ListItem>
            </List>
          )}
          {!lodashIsEmpty(filteredResults) ? (
            Object.entries(filteredResults).map(
              ([key, catalog]) => catalog.length > 0 && renderIndexContents(key, catalog, true),
            )
          ) : (
            <div className={classes.loadingPlaceholder}>
              {/* <CircularProgress /> */}
              Loading Designers...
            </div>
          )}
        </BackToTop>
      )}
      {isValidSearch && searchResults.length > 0 && renderIndexContents('', searchResults, false)}
      {isValidSearch && searchResults.length === 0 && (
        <Typography className={classes.noResults} variant="h2">
          No Results
        </Typography>
      )}
    </>
  );
};

DesignerNavigation.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  /** History object from react-router. */
  history: PropTypes.shape({}),
  designerCategory: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      id: PropTypes.string,
      children: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ),
  searchPage: PropTypes.bool,
  checkedItems: PropTypes.arrayOf(PropTypes.string),
  checkboxClickHandler: PropTypes.func,
  checkboxMode: PropTypes.bool,
};

export default DesignerNavigation;
